import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useRef, useState } from "react";
import EmailEditor, { EditorRef, EmailEditorProps } from "react-email-editor";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as yup from "yup";

import { EmailTemplateApi } from "api/emailtemplates/emailtemplatesApi";
import { useCoursesOptions } from "api/hooks/useCourses";
import { useEmailTemplateOptions } from "api/hooks/useEmailTemplateOptions";
import { useCreateScheduledEmail, useCreateNewsletterSend, useScheduledEmail, useUpdateScheduledEmail } from "api/hooks/useScheduledEmails";
import { useUsersOptions } from "api/hooks/useUsers";
import {
  CreateScheduledEmailRequest,
  CreateNewsletterSendRequest,
  RecipientRequest,
  UpdateScheduledEmailRequest,
} from "api/scheduledemails/scheduledemailsApi";
import { PaziaBox, PaziaBreadcrumbs } from "components/atoms";
import { FormSection } from "components/FormSection";
import { routes } from "consts";
import { templateHasCoursePlaceholders } from "consts/emailTemplateType";
import { PaziaButton, PaziaFormButton, PaziaInput, PaziaLoader, PaziaSelect } from "components/atoms";
import { useToaster } from "utils";
import { useThemeProvider } from "utils/ThemeContext";
import { RecipientSelector } from "./RecipientSelector";
import { TemplateSelector } from "./components";

const schema = yup.object().shape({
  title: yup.string().required("Nazwa jest wymagana").max(255, "Nazwa może mieć maksymalnie 255 znaków"),
  subject: yup.string().required("Temat jest wymagany").max(500, "Temat może mieć maksymalnie 500 znaków"),
  isNewsletterSend: yup.boolean().optional(),
  recipients: yup
    .array()
    .of(yup.mixed().required())
    .when("isNewsletterSend", {
      is: true,
      then: (s) => s.min(0),
      otherwise: (s) => s.min(1, "Wybierz co najmniej jednego odbiorcę").max(1000, "Maksymalnie 1000 odbiorców"),
    })
    .required("Odbiorcy są wymagani"),
  selectedTemplate: yup.string().optional(),
  courseId: yup.number().optional(),
  courseRequired: yup.boolean().optional(),
});

type FormValues = {
  title: string;
  subject: string;
  isNewsletterSend: boolean;
  recipients: Array<number | { userId: null; emailAddress: string; name?: string }>;
  selectedTemplate?: string;
  courseId?: number;
  courseRequired?: boolean;
};

interface ScheduledEmailFormProps {
  isEdit?: boolean;
}

export const ScheduledEmailForm = ({ isEdit = false }: ScheduledEmailFormProps) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentTheme } = useThemeProvider();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError } = useToaster();
  const emailEditorRef = useRef<EditorRef>(null);
  const [htmlBody, setHtmlBody] = useState("");
  const [courseRequired, setCourseRequired] = useState(false);

  const { isLoading: usersLoading, options: userOptions } = useUsersOptions();
  const { scheduledEmail, isLoading: emailLoading } = useScheduledEmail(isEdit ? Number(id) : 0);
  const { templates } = useEmailTemplateOptions();
  const { isLoading: coursesLoading, options: courseOptions } = useCoursesOptions();

  const createMutation = useCreateScheduledEmail();
  const updateMutation = useUpdateScheduledEmail();
  const newsletterMutation = useCreateNewsletterSend();

  // Helper function to get default date in Polish timezone
  const getDefaultDate = () => {
    const now = new Date();
    const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
    return oneHourLater;
  };

  // Helper function to get default time
  const getDefaultTime = () => {
    const now = new Date();
    const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
    return `${oneHourLater.getHours().toString().padStart(2, "0")}:${oneHourLater.getMinutes().toString().padStart(2, "0")}`;
  };

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      title: scheduledEmail?.title || "",
      subject: scheduledEmail?.subject || "",
      isNewsletterSend: false,
      recipients: [],
      selectedTemplate: "",
      courseId: undefined,
      courseRequired: false,
    },
  });

  const {
    handleSubmit,
    formState: { errors },
    setValue,
  } = methods;

  // Handle template selection and loading
  const handleTemplateSelect = (template: EmailTemplateApi) => {
    try {
      // Load template subject into form
      setValue("subject", template.subject);

      // Check if template has course placeholders and update requirement
      const hasCourseePlaceholders = templateHasCoursePlaceholders(template.type as any);
      setCourseRequired(hasCourseePlaceholders);
      setValue("courseRequired", hasCourseePlaceholders);

      // Clear course selection when switching to template that doesn't require it
      if (!hasCourseePlaceholders) {
        setValue("courseId", undefined);
      }

      // Load template design into email editor
      if (template.jsonBody && emailEditorRef.current?.editor) {
        const designData = JSON.parse(template.jsonBody);
        emailEditorRef.current.editor.loadDesign(designData);
        setHtmlBody(template.htmlBody);
      }

      // Clear the template selector
      setValue("selectedTemplate", "");

      showSuccess(`Szablon "${template.name}" został załadowany`);
    } catch (error) {
      console.error("Failed to load template:", error);
      showError("Nie udało się załadować szablonu");
    }
  };

  // Handle URL parameter for template loading
  useEffect(() => {
    const templateId = searchParams.get("templateId");
    if (templateId && !isEdit && templates.length > 0) {
      const template = templates.find((t) => t.id === parseInt(templateId));
      if (template) {
        // Auto-load template from URL parameter
        handleTemplateSelect(template);

        // Remove the templateId from URL to clean up
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("templateId");
        navigate({ search: newSearchParams.toString() }, { replace: true });
      }
    }
  }, [searchParams, isEdit, templates, navigate]);

  const onReady: EmailEditorProps["onReady"] = (unlayer) => {
    if (scheduledEmail?.jsonBody) {
      try {
        unlayer.loadDesign(JSON.parse(scheduledEmail.jsonBody));
      } catch (e) {
        console.warn("Failed to load email design:", e);
      }
    }

    if (scheduledEmail?.htmlBody) {
      setHtmlBody(scheduledEmail.htmlBody);
    }
  };

  const onSubmitHandler = async (data: FormValues) => {
    try {
      // Validate course requirement if template has course placeholders
      if (courseRequired && !data.courseId) {
        showError("Kurs jest wymagany dla wybranego szablonu");
        return;
      }

      //@ts-ignore
      emailEditorRef.current?.editor?.exportHtml((htmlData) => {
        const { design, html } = htmlData;

        if (data.isNewsletterSend) {
          const newsletterData: CreateNewsletterSendRequest = {
            title: data.title,
            subject: data.subject,
            htmlBody: html,
            jsonBody: JSON.stringify(design),
          };
          newsletterMutation.mutate(newsletterData, {
            onSuccess: () => {
              showSuccess("Newsletter został utworzony");
              navigate(routes.EMAILS + routes.SCHEDULED_EMAILS);
            },
            onError: () => {
              showError("Wystąpił błąd podczas tworzenia newslettera");
            },
          });
          return;
        }

        const recipients: RecipientRequest[] = data.recipients.map((item) => {
          if (typeof item === "number") {
            const user = userOptions.find((u) => u.value === item);
            return {
              userId: item,
              emailAddress: user?.user?.email || "",
            };
          }
          return item as RecipientRequest;
        });

        const emailData = {
          title: data.title,
          subject: data.subject,
          htmlBody: html,
          jsonBody: JSON.stringify(design),
          recipients,
          ...(data.courseId && { courseId: data.courseId }),
        };

        if (isEdit && id) {
          const updateData: UpdateScheduledEmailRequest = emailData;
          updateMutation.mutate(
            { id: Number(id), data: updateData },
            {
              onSuccess: () => {
                showSuccess("Email został zaktualizowany");
                navigate(routes.EMAILS + routes.SCHEDULED_EMAILS);
              },
              onError: () => {
                showError("Wystąpił błąd podczas aktualizacji emaila");
              },
            },
          );
        } else {
          const createData: CreateScheduledEmailRequest = { ...emailData };
          createMutation.mutate(createData, {
            onSuccess: () => {
              showSuccess("Email został utworzony");
              navigate(routes.EMAILS + routes.SCHEDULED_EMAILS);
            },
            onError: () => {
              showError("Wystąpił błąd podczas tworzenia emaila");
            },
          });
        }
      });
    } catch (error) {
      showError("Wystąpił błąd podczas przetwarzania formularza");
    }
  };

  if ((isEdit && emailLoading) || usersLoading) {
    return <PaziaLoader position="center" />;
  }

  return (
    <div>
      <PaziaBreadcrumbs
        className="mb-2"
        segments={[
          { title: "Emails", path: routes.EMAILS + routes.SCHEDULED_EMAILS },
          { title: isEdit ? "Edytuj" : "Utwórz" },
        ]}
      />
      <PaziaBox className="mt-2">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmitHandler)} className="space-y-8">
            {/* Email Content Section */}
            <FormSection
              number={1}
              title={isEdit ? "Edytuj zaplanowany email" : "Utwórz zaplanowany email"}
              color="orange"
            >
              <PaziaInput name="title" placeholder="Wprowadź nazwę wysyłki..." required />
            </FormSection>

            {/* Recipients Section */}
            <FormSection number={2} title="Odbiorcy" color="blue">
              <RecipientSelector name="recipients" error={errors.recipients?.message} />
            </FormSection>

            {/* Template & Course Section */}
            <FormSection number={3} title="Szablon i kontekst" color="purple">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div>
                  <TemplateSelector onTemplateSelect={handleTemplateSelect} disabled={isEdit} />
                </div>
                <div>
                  <PaziaSelect
                    name="courseId"
                    label={courseRequired ? "Kurs (wymagany dla wybranego szablonu)" : "Kurs (opcjonalnie)"}
                    placeholder="Wybierz kurs..."
                    options={courseOptions}
                    isLoading={coursesLoading}
                    required={courseRequired}
                  />
                  {courseRequired ? (
                    <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                      Wybrany szablon zawiera zmienne związane z kursem. Wybierz kurs, aby poprawnie wypełnić te
                      zmienne.
                    </p>
                  ) : (
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Opcjonalnie wybierz kurs, jeśli email dotyczy konkretnego kursu.
                    </p>
                  )}
                </div>
              </div>
            </FormSection>

            {/* Email Details Section */}
            <FormSection number={4} title="Szczegóły emaila" color="green">
              <div className="mb-4 space-y-4">
                <PaziaInput label="Temat emaila" name="subject" placeholder="Wprowadź temat emaila" required />
              </div>
              <div className="overflow-hidden rounded-md border border-gray-300 dark:border-gray-600">
                <EmailEditor
                  ref={emailEditorRef}
                  onReady={onReady}
                  style={{ height: "600px" }}
                  options={{
                    displayMode: "email",
                    locale: "pl-PL",
                    appearance: { theme: currentTheme === "dark" ? "modern_dark" : "modern_light" },
                  }}
                />
              </div>
            </FormSection>

            {/* Form Actions */}
            <div className="flex justify-end space-x-4 border-t border-gray-200 pt-6 dark:border-gray-700">
              <PaziaButton type="secondary" onClick={() => navigate(routes.EMAILS + routes.SCHEDULED_EMAILS)}>
                Anuluj
              </PaziaButton>
              <PaziaFormButton type="primary">{isEdit ? "Zaktualizuj email" : "Utwórz email"}</PaziaFormButton>
            </div>
          </form>
        </FormProvider>
      </PaziaBox>
    </div>
  );
};
